import { useState, useEffect, useMemo } from 'react';

// Hooks
import { useAppDispatch } from '../../misc/hooks'

// Actions
import { getAllUsers } from '../../slice/admin'
import { abtestGetAnalytics } from '../../slice/abtest'
import Analytics from '../../components/analytics/analytics';

// ---------- Types & config ----------

type View = 'live' | 'users' | 'inactive' | 'json'

const STATUS_ORDER: Record<string, number> = { live: 0, scheduled: 1, paused: 2, draft: 3, completed: 4 }

// ---------- Helpers ----------

const formatDate = (date: any) => {
	if (!date) return '—'
	try {
		return new Intl.DateTimeFormat(navigator?.language || 'fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date))
	} catch (e) {
		return '—'
	}
}

const daysBetween = (from: any, to: any) => Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000)

const daysLeft = (end: any) => {
	if (!end) return ''
	const d = daysBetween(Date.now(), end)
	if (d < 0) return 'ended'
	if (d === 0) return 'ends today'
	return `${d} day${d > 1 ? 's' : ''} left`
}

const liveSince = (date: any) => {
	if (!date) return ''
	const d = daysBetween(date, Date.now())
	return d <= 0 ? 'started today' : `day ${d}`
}

const shortShop = (shop: any) => String(shop ?? '').replace(/\.myshopify\.com$/, '')

const normalize = (s: any): string =>
	String(s ?? '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/\.myshopify\.com$/, '')
		.trim();

const levenshtein = (a: string, b: string): number => {
	const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i]);
	for (let j = 1; j <= b.length; j++) dp[0][j] = j;
	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			dp[i][j] = Math.min(
				dp[i - 1][j] + 1,
				dp[i][j - 1] + 1,
				dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
			);
		}
	}
	return dp[a.length][b.length];
};

// exact > commence par > contient > ressemble
const score = (shopName: string, query: string): number => {
	if (shopName === query) return 100;
	if (shopName.startsWith(query)) return 80;
	if (shopName.includes(query)) return 60;
	const maxDist = query.length <= 4 ? 1 : 2;
	if (levenshtein(shopName, query) <= maxDist) return 40;
	return 0;
};

const searchUsers = (list: any[], value: string) => {
	const query = normalize(value)
	if (!query) return list

	// On garde le meilleur score entre le shop et l'email
	const scored = list
		.map((user: any) => ({
			user,
			s: Math.max(
				score(normalize(user.shop), query),
				user.email ? score(normalize(user.email), query) : 0
			),
		}))
		.filter((x: any) => x.s > 0)

	const exact = scored.find((x: any) => x.s === 100)
	return exact
		? [exact.user]
		: scored.sort((a: any, b: any) => b.s - a.s).map((x: any) => x.user)
}

const sortTests = (a: any, b: any) => {
	const rank = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5)
	if (rank !== 0) return rank
	return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
}

function Admin() {
	const dispatch = useAppDispatch()

	// State
	const [allUsers, setAllUsers] = useState<any[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [shop, setShop] = useState<string>('')
	const [view, setView] = useState<View>('live')
	const [openUsers, setOpenUsers] = useState<Record<string, boolean>>({})
	const [jsonView, setJsonView] = useState<'users' | 'tests' | null>(null)

	// Analytics panel
	const [analytics, setAnalytics] = useState<any>()
	const [analyticsTest, setAnalyticsTest] = useState<any>(null)
	const [isLoadingAnalytics, setIsLoadingAnalytics] = useState<boolean>(false)

	useEffect(() => {
		const launch = async () => {
			let res: any = await dispatch(getAllUsers())
			setIsLoading(false)

			if (res?.payload?.length) {
				const sorted = [...res.payload].sort((a: any, b: any) => {
					// installés en premier, puis du plus récent au plus ancien
					if (a.is_app_install !== b.is_app_install) return a.is_app_install ? -1 : 1
					return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				})
				setAllUsers(sorted)
			}
		}
		launch()
	}, [dispatch]);

	// Fermer le panneau stats avec Échap
	useEffect(() => {
		if (!analyticsTest) return
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAnalytics() }
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [analyticsTest])

	// ---------- Données dérivées ----------

	const users = useMemo(() => searchUsers(allUsers, shop), [allUsers, shop])
	// Users installés : ceux avec un test live d'abord, puis scheduled / paused, puis le reste
	// (tri stable : l'ordre de la recherche est conservé dans chaque groupe)
	const activityRank = (u: any) => {
		const statuses = (u.abtests ?? []).map((t: any) => t.status)
		if (statuses.includes('live')) return 0
		if (statuses.includes('scheduled') || statuses.includes('paused')) return 1
		return 2
	}
	const activeUsers = useMemo(() =>
		users
			.filter((u: any) => u?.is_app_install)
			.sort((a: any, b: any) => activityRank(a) - activityRank(b))
	, [users])
	const inactiveUsers = useMemo(() => users.filter((u: any) => !u?.is_app_install), [users])

	// Tous les tests, avec le shop de leur user
	const tests = useMemo(() =>
		users
			.flatMap((u: any) => (u.abtests ?? []).map((t: any) => ({ ...t, shop: u.shop, email: u.email })))
			.sort(sortTests)
	, [users])

	const liveTests = useMemo(() =>
		tests
			.filter((t: any) => t.status === 'live')
			// se termine bientôt en premier
			.sort((a: any, b: any) => new Date(a.end_at || 8.64e15).getTime() - new Date(b.end_at || 8.64e15).getTime())
	, [tests])

	const totalActive = allUsers.filter((u: any) => u?.is_app_install).length
	const totalLive = allUsers.flatMap((u: any) => u.abtests ?? []).filter((t: any) => t.status === 'live').length

	// ---------- Actions ----------

	const getAnalytics = async (test: any) => {
		setAnalyticsTest(test)
		setAnalytics(undefined)
		setIsLoadingAnalytics(true)
		let resAnalytics: any = await dispatch(abtestGetAnalytics(test.id))
		setIsLoadingAnalytics(false)
		if (resAnalytics?.payload) {
			setAnalytics(resAnalytics.payload)
		}
	}

	const closeAnalytics = () => {
		setAnalyticsTest(null)
		setAnalytics(undefined)
	}

	const toggleUser = (key: string) => setOpenUsers((o) => ({ ...o, [key]: !o[key] }))

	// ---------- Rendu ----------

	const statsButton = (test: any) => (
		<button className="admin__btn" onClick={() => getAnalytics(test)}>
			See stats
		</button>
	)

	const statusBadge = (status: string) => (
		<span className={`admin__status ${status || ''}`}>{status || 'unknown'}</span>
	)

	return (
		<div className="admin">

			{/* ---------- KPIs ---------- */}
			<div className="admin__kpis">
				<button className={`admin__kpi live ${view === 'live' ? 'active' : ''}`} onClick={() => setView('live')}>
					<span className="admin__kpi-value">{totalLive}</span>
					<span className="admin__kpi-label">Live tests</span>
				</button>
				<button className={`admin__kpi ${view === 'users' ? 'active' : ''}`} onClick={() => setView('users')}>
					<span className="admin__kpi-value">{totalActive}</span>
					<span className="admin__kpi-label">Active users</span>
				</button>
				<button className={`admin__kpi ${view === 'inactive' ? 'active' : ''}`} onClick={() => setView('inactive')}>
					<span className="admin__kpi-value">{allUsers.length - totalActive}</span>
					<span className="admin__kpi-label">Uninstalled</span>
				</button>
			</div>

			{/* ---------- Search + views ---------- */}
			<div className="admin__toolbar">
				<label className="admin__search">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
						<circle cx="11" cy="11" r="7"></circle>
						<path d="M20 20l-3.5-3.5"></path>
					</svg>
					<input
						type="search"
						placeholder="Search a shop or an email"
						value={shop}
						onChange={(e) => setShop(e.target.value)}
						aria-label="Search a shop or an email"
					/>
				</label>

				<div className="admin__views" role="tablist">
					{([
						['live', `Live (${liveTests.length})`],
						['users', `Users (${activeUsers.length})`],
						['inactive', `Uninstalled (${inactiveUsers.length})`],
						['json', 'JSON'],
					] as [View, string][]).map(([key, label]) => (
						<button
							key={key}
							role="tab"
							aria-selected={view === key}
							className={`admin__view ${view === key ? 'active' : ''}`}
							onClick={() => setView(key)}
						>
							{label}
						</button>
					))}
				</div>
			</div>

			{isLoading ?
				<div className="admin__empty">Loading…</div>
			: <>

				{/* ---------- LIVE TESTS ---------- */}
				{view === 'live' &&
					(liveTests.length ?
						<div className="admin__grid">
							{liveTests.map((t: any) => (
								<div className="admin__card live" key={t.id}>
									<div className="admin__card-top">
										{statusBadge('live')}
										<span className="admin__muted">{liveSince(t.scheduled_at)}</span>
									</div>
									<div className="admin__card-shop">{shortShop(t.shop)}</div>
									{t.email ?
										<a className="admin__email" href={`mailto:${t.email}`}>{t.email}</a>
									: ''}
									<div className="admin__card-name">{t.name || 'Untitled test'}</div>
									<div className="admin__card-meta">
										<span>Ends {formatDate(t.end_at)}</span>
										<span className="admin__strong">{daysLeft(t.end_at)}</span>
									</div>
									{t.test?.[1]?.recipe_live ?
										<details className="admin__recipe" open>
											<summary>Client's test (recipe)</summary>
											<pre className="admin__pre">{JSON.stringify(t.test[1].recipe_live, null, 2)}</pre>
										</details>
									:
										<span className="admin__muted">No recipe</span>
									}
									{statsButton(t)}
								</div>
							))}
						</div>
					:
						<div className="admin__empty">{shop ? 'No live test for this search.' : 'No live test right now.'}</div>
					)
				}

				{/* ---------- ACTIVE USERS ---------- */}
				{view === 'users' &&
					(activeUsers.length ?
						<div className="admin__list">
							{activeUsers.map((u: any) => {
								const key = u.id ?? u.shop
								const isOpen = openUsers[key] ?? activeUsers.length === 1
								const userTests = [...(u.abtests ?? [])].sort(sortTests)
								const liveCount = userTests.filter((t: any) => t.status === 'live').length

								return (
									<div className={`admin__user ${isOpen ? 'open' : ''}`} key={key}>
										<button className="admin__user-head" onClick={() => toggleUser(key)} aria-expanded={isOpen}>
											<div className="admin__user-main">
												<span className="admin__user-shop">{shortShop(u.shop)}</span>
												<span className="admin__muted">{u.email || '—'}</span>
											</div>
											<div className="admin__user-tags">
												{liveCount ? <span className="admin__status live">{liveCount} live</span> : ''}
												<span className="admin__muted">{userTests.length} test{userTests.length > 1 ? 's' : ''}</span>
												<svg className="admin__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>
											</div>
										</button>

										{isOpen &&
											<div className="admin__user-body">
												<dl className="admin__info">
													<div><dt>Shop</dt><dd>{u.shop || '—'}</dd></div>
													<div><dt>Email</dt><dd>{u.email ? <a className="admin__email" href={`mailto:${u.email}`}>{u.email}</a> : '—'}</dd></div>
													<div><dt>Source</dt><dd>{u.source || '—'}</dd></div>
													<div><dt>Review</dt><dd>{u.review ? "Demande en cours" : ""}</dd></div>
													<div><dt>Installed</dt><dd>{formatDate(u.created_at)}</dd></div>
													<div><dt>Updated</dt><dd>{formatDate(u.updated_at)}</dd></div>
												</dl>

												{userTests.length ?
													<div className="admin__tests">
														{userTests.map((t: any) => (
															<div className={`admin__test ${t.status || ''}`} key={t.id}>
																<div className="admin__test-main">
																	<div className="admin__test-title">
																		{statusBadge(t.status)}
																		<span className="admin__strong">{t.name || 'Untitled test'}</span>
																	</div>
																	<div className="admin__test-meta">
																		<span>#{t.id}</span>
																		<span>Created {formatDate(t.created_at)}</span>
																		<span>Ends {formatDate(t.end_at)}</span>
																	</div>
																	{t.test?.[1]?.recipe_live ?
																		<details className="admin__recipe">
																			<summary>Recipe</summary>
																			<pre className="admin__pre">{JSON.stringify(t.test[1].recipe_live, null, 2)}</pre>
																		</details>
																	: ''}
																</div>
																{statsButton({ ...t, shop: u.shop, email: u.email })}
															</div>
														))}
													</div>
												:
													<div className="admin__muted">No test yet.</div>
												}
											</div>
										}
									</div>
								)
							})}
						</div>
					:
						<div className="admin__empty">No active user for this search.</div>
					)
				}

				{/* ---------- INACTIVE USERS ---------- */}
				{view === 'inactive' &&
					(inactiveUsers.length ?
						<div className="admin__table">
							{inactiveUsers.map((u: any) => (
								<div className="admin__row" key={u.id ?? u.shop}>
									<span className="admin__strong">{shortShop(u.shop)}</span>
									<span className="admin__muted">{u.email || '—'}</span>
									<span className="admin__muted">{u.source || '—'}</span>
									<span className="admin__muted">Installed {formatDate(u.created_at)}</span>
									<span className="admin__muted">{(u.abtests ?? []).length} tests</span>
								</div>
							))}
						</div>
					:
						<div className="admin__empty">No uninstalled user for this search.</div>
					)
				}

				{/* ---------- JSON ---------- */}
				{view === 'json' &&
					<div className="admin__json">
						<div className="admin__views">
							<button className={`admin__view ${jsonView === 'users' ? 'active' : ''}`} onClick={() => setJsonView(jsonView === 'users' ? null : 'users')}>
								Users JSON
							</button>
							<button className={`admin__view ${jsonView === 'tests' ? 'active' : ''}`} onClick={() => setJsonView(jsonView === 'tests' ? null : 'tests')}>
								Tests JSON
							</button>
						</div>
						{jsonView ?
							<pre className="admin__pre big">{JSON.stringify(jsonView === 'users' ? users : tests, null, 2)}</pre>
						: ''}
					</div>
				}
			</>}

			{/* ---------- ANALYTICS PANEL ---------- */}
			{analyticsTest &&
				<div className="admin__overlay" onClick={closeAnalytics}>
					<div className="admin__panel" role="dialog" aria-modal="true" aria-label="Test stats" onClick={(e) => e.stopPropagation()}>
						<div className="admin__panel-head">
							<div>
								<div className="admin__panel-shop">
									{shortShop(analyticsTest.shop)}
									{analyticsTest.email ? ` · ${analyticsTest.email}` : ''}
								</div>
								<div className="admin__panel-title">
									{statusBadge(analyticsTest.status)}
									<span>{analyticsTest.name || 'Untitled test'}</span>
								</div>
							</div>
							<button className="admin__close" onClick={closeAnalytics} aria-label="Close">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
							</button>
						</div>

						<div className="admin__panel-body">
							{isLoadingAnalytics ?
								<div className="admin__empty">Loading stats…</div>
							: analytics ?
								<Analytics analytics={analytics} status={analyticsTest.status} />
							:
								<div className="admin__empty">No stats for this test.</div>
							}
						</div>
					</div>
				</div>
			}
		</div>
	);
}

export default Admin;